// La page Tickets : orchestrateur : état, chargements, temps réel, liens profonds. Le rendu est
// délégué aux modules du dossier (TicketListCard, TicketDetail → fil, composeur, assignés).
// L'état et le flux de données sont IDENTIQUES à l'ancienne page monolithique.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { FiCheckCircle, FiInbox, FiPlus, FiUser } from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { usePermissions } from "../../Auth/PermissionsContext";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { TicketCreateModal } from "../../Components/Tickets/TicketCreateModal";
import { client } from "../../Clients";
import { notificationsClient } from "../Notifications/notificationsClient";
import { notifyNotificationsChanged } from "../../Realtime/useNotifications";
import { CardSkeleton } from "./TicketBits";
import { TicketListCard } from "./TicketListCard";
import { TicketDetail } from "./TicketDetail";
import { ticketsClient, type Ticket, type TicketCategory, type TicketComment } from "./ticketsClient";

const STATUS_TABS: Array<{ key: string; label: string }> = [
	{ key: "open", label: "Ouverts" },
	{ key: "closed", label: "Fermés" },
	{ key: "", label: "Tous" }
];

/** Prefill of the create modal, resolved from a ?nouveau=1 deep link (public preview widget). */
type CreatePrefill = {
	title: string;
	description: string;
	linkedContentId: string | null;
	linkedContentType: string | null;
	linkedContentTitle: string | null;
};

const Tickets: React.FC = () => {
	usePageHeader({ title: "Tickets" });
	const location = useLocation();
	const history = useHistory();
	const { can } = usePermissions();
	const canWrite = can("feature:tickets:write");
	// E-mail de l'opérateur connecté : sert uniquement à teinter/aligner SES messages dans le fil.
	const { user } = useAuth0();
	const currentUserEmail = user?.email ?? null;

	const [categories, setCategories] = useState<TicketCategory[]>([]);
	const [status, setStatus] = useState<string>("open");
	const [category, setCategory] = useState<string>("");
	const [mine, setMine] = useState(false);
	const [items, setItems] = useState<Ticket[]>([]);
	const [total, setTotal] = useState(0);
	const [counts, setCounts] = useState<{ open: number; closed: number } | null>(null);
	const [loading, setLoading] = useState(true);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [detail, setDetail] = useState<{ ticket: Ticket; comments: TicketComment[] } | null>(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [comment, setComment] = useState("");
	const [sendingComment, setSendingComment] = useState(false);
	const [showCreate, setShowCreate] = useState(false);
	const [createPrefill, setCreatePrefill] = useState<CreatePrefill | null>(null);
	const deepLinkHandled = useRef(false);

	const categoryLabel = useMemo(() => {
		const map = new Map(categories.map((c) => [c.key, c.label]));
		return (key: string) => map.get(key) ?? key;
	}, [categories]);

	useEffect(() => {
		ticketsClient.categories().then(setCategories).catch(() => setCategories([]));
	}, []);

	const loadList = useCallback(async () => {
		setLoading(true);
		try {
			const res = await ticketsClient.list({ status: status || undefined, category: category || undefined, mine, pageSize: 100 });
			setItems(res.items);
			setTotal(res.total);
		} catch {
			setItems([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}, [status, category, mine]);

	/** Compteurs globaux (ouverts / fermés) de l'en-tête : deux requêtes pageSize=1, seul `total` compte. */
	const loadCounts = useCallback(async () => {
		try {
			const [open, closed] = await Promise.all([
				ticketsClient.list({ status: "open", pageSize: 1 }),
				ticketsClient.list({ status: "closed", pageSize: 1 })
			]);
			setCounts({ open: open.total, closed: closed.total });
		} catch {
			/* transitoire : on garde les derniers compteurs connus */
		}
	}, []);

	const loadDetail = useCallback(async (id: string) => {
		setDetailLoading(true);
		try {
			setDetail(await ticketsClient.get(id));
		} catch {
			setDetail(null);
		} finally {
			setDetailLoading(false);
		}
	}, []);

	useEffect(() => {
		loadList();
	}, [loadList]);

	useEffect(() => {
		loadCounts();
	}, [loadCounts]);

	// Lien profond : /tickets?ticket={id} (ex. « Gérer dans Tickets » depuis une page de contenu) ouvre
	// ce ticket directement. Le détail est chargé par id indépendamment des filtres de liste, donc le
	// ticket lié apparaît même si l'onglet/filtre courant l'exclut.
	useEffect(() => {
		const id = new URLSearchParams(location.search).get("ticket");
		if (id) setSelectedId(id);
	}, [location.search]);

	// Lien profond de CRÉATION : /tickets?nouveau=1&titre=&url=&contentId=&contentType= (records) ou
	// &slug=&lang=&path= (pages : le payload public d'une page ne porte pas d'id admin : on le résout
	// ici via GET /api/content/page/{lang}?slug=, puis via le convertisseur
	// GET /api/content/resolve-editor?path= quand le slug baké est absent ou périmé). Ouvre la modale
	// pré-remplie (titre + description avec l'URL publique + contenu lié) puis NETTOIE la query
	// (history.replace) pour qu'un refresh ou un retour arrière ne rouvre pas la modale. Résolution
	// best-effort : un échec ouvre quand même la modale, simplement sans contenu lié.
	useEffect(() => {
		if (deepLinkHandled.current) return;
		const params = new URLSearchParams(location.search);
		if (params.get("nouveau") !== "1") return;
		deepLinkHandled.current = true;

		const titre = params.get("titre") ?? "";
		const publicUrl = params.get("url") ?? "";
		const contentId = params.get("contentId");
		const contentType = params.get("contentType");
		const pageSlug = params.get("slug");
		const pagePath = params.get("path");
		const pageLang = (params.get("lang") ?? "fr").toLowerCase();

		// La query est consommée : on la retire de l'URL tout de suite (modale ou pas).
		history.replace(location.pathname);
		if (!canWrite) return;

		void (async () => {
			let linked: { id: string; type: string; title: string | null } | null = null;
			if (contentId) {
				linked = { id: contentId, type: contentType ?? "", title: titre || contentId };
			} else if (pageSlug) {
				try {
					const res = await client.content.apiContentSchemaKeyLanguageGet("page", pageLang, pageSlug, {
						withCredentials: true,
						headers: { Accept: "application/json" }
					});
					const doc = res.data?.current;
					if (doc?.id) {
						linked = {
							id: doc.id,
							type: "page",
							title: (doc.data?.title as string | undefined) || titre || pageSlug
						};
					}
				} catch {
					/* page introuvable : le convertisseur par chemin ci-dessous tente encore */
				}
			}
			if (!linked && pagePath) {
				try {
					const res = await axios.get("/api/content/resolve-editor", {
						params: { path: pagePath, language: pageLang }
					});
					const t = res.data ?? {};
					if (t.id) {
						linked = { id: t.id, type: t.schemaKey || "page", title: t.title || titre || pagePath };
					}
				} catch {
					/* chemin introuvable / API indisponible : ticket sans contenu lié */
				}
			}
			setCreatePrefill({
				title: titre,
				description: publicUrl ? `Page concernée : ${publicUrl}` : "",
				linkedContentId: linked?.id ?? null,
				linkedContentType: linked?.type ?? null,
				linkedContentTitle: linked?.title ?? null
			});
			setShowCreate(true);
		})();
	}, [location.search, location.pathname, history, canWrite]);

	useEffect(() => {
		if (selectedId) {
			setDetail(null); // vide → le squelette s'affiche pendant le chargement du nouveau ticket
			loadDetail(selectedId);
			// Ouvrir le ticket = ses notifications sont lues pour MOI (création, réponses…). Le serveur
			// pousse ensuite l'événement « notification » phase read → la cloche et la pastille de la
			// carte se resynchronisent partout, y compris dans les autres onglets. Best-effort.
			notificationsClient
				.markReadBySource(`ticket:${selectedId}:`)
				.then((count) => {
					if (count > 0) notifyNotificationsChanged();
				})
				.catch(() => undefined);
		} else {
			setDetail(null);
		}
	}, [selectedId, loadDetail]);

	// Entièrement temps réel : tout changement de ticket rafraîchit la liste, les compteurs et le détail ouvert.
	useRealtimeEvent("ticket", () => {
		loadList();
		loadCounts();
		if (selectedId) loadDetail(selectedId);
	});

	const addComment = async () => {
		if (!selectedId || !comment.trim() || sendingComment) return;
		setSendingComment(true);
		try {
			await ticketsClient.comment(selectedId, comment.trim());
			setComment("");
			loadDetail(selectedId);
		} catch {
			toast.error("Échec de l'ajout du commentaire.");
		} finally {
			setSendingComment(false);
		}
	};

	const setStatusOf = async (t: Ticket, close: boolean) => {
		try {
			await (close ? ticketsClient.close(t.id) : ticketsClient.reopen(t.id));
			loadDetail(t.id);
			loadList();
			loadCounts();
		} catch {
			toast.error("Échec du changement de statut.");
		}
	};

	const remove = async (t: Ticket) => {
		if (!window.confirm("Supprimer définitivement ce ticket ? Cette action est irréversible.")) return;
		try {
			await ticketsClient.remove(t.id);
			toast.success("Ticket supprimé.");
			setSelectedId(null);
			loadList();
			loadCounts();
		} catch {
			toast.error("Échec de la suppression.");
		}
	};

	const isDefaultOpenView = status === "open" && !category && !mine;

	return (
		<div className="mx-auto max-w-7xl space-y-4 p-6">
			{/* ---- En-tête : titre, compteurs globaux, action de création, barre de filtres ---- */}
			<header className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
				<div className="flex flex-wrap items-center gap-3">
					<div className="min-w-0">
						<p className="text-xs uppercase tracking-widest text-slate-500">Collaboration</p>
						<h1 className="text-2xl font-semibold text-slate-900">Tickets</h1>
						<p className="text-sm text-slate-500">
							Signalez un problème, suivez son traitement et gardez toute la discussion au même endroit.
						</p>
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-2">
						{counts && (
							<>
								<span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700">
									<span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
									{counts.open.toLocaleString("fr-FR")} ouvert{counts.open > 1 ? "s" : ""}
								</span>
								<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-500">
									<FiCheckCircle className="h-3.5 w-3.5" />
									{counts.closed.toLocaleString("fr-FR")} fermé{counts.closed > 1 ? "s" : ""}
								</span>
							</>
						)}
						{canWrite && (
							<button
								type="button"
								onClick={() => setShowCreate(true)}
								className="inline-flex items-center gap-1.5 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004a87]"
							>
								<FiPlus /> Nouveau ticket
							</button>
						)}
					</div>
				</div>

				<div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
					<div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 shadow-sm">
						{STATUS_TABS.map((tab) => (
							<button
								key={tab.key || "all"}
								type="button"
								onClick={() => setStatus(tab.key)}
								className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
									status === tab.key ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
								}`}
							>
								{tab.label}
							</button>
						))}
					</div>
					<select
						value={category}
						onChange={(e) => setCategory(e.target.value)}
						className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition focus:border-slate-400 focus:outline-none"
					>
						<option value="">Toutes catégories</option>
						{categories.map((c) => (
							<option key={c.key} value={c.key}>{c.label}</option>
						))}
					</select>
					<button
						type="button"
						aria-pressed={mine}
						onClick={() => setMine((m) => !m)}
						className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold shadow-sm transition ${
							mine
								? "border-[#005CA9] bg-[#005CA9]/10 text-[#005CA9]"
								: "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
						}`}
					>
						<FiUser className="h-3.5 w-3.5" /> Mes tickets
					</button>
					<span className="ml-auto text-sm text-slate-400">
						{loading ? "…" : `${total.toLocaleString("fr-FR")} ticket${total > 1 ? "s" : ""}`}
					</span>
				</div>
			</header>

			<div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
				{/* ---- Liste ---- */}
				<div className="space-y-2.5">
					{loading ? (
						<>
							<CardSkeleton />
							<CardSkeleton />
							<CardSkeleton />
							<CardSkeleton />
						</>
					) : items.length === 0 ? (
						<div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
							{isDefaultOpenView ? (
								<>
									<span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
										<FiCheckCircle className="h-6 w-6" />
									</span>
									<p className="mt-3 text-sm font-semibold text-slate-700">Aucun ticket ouvert : tout est traité.</p>
									<p className="mt-1 text-xs text-slate-400">
										Créez un ticket pour signaler un problème ou confier une tâche à l'équipe.
									</p>
									{canWrite && (
										<button
											type="button"
											onClick={() => setShowCreate(true)}
											className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
										>
											<FiPlus className="h-4 w-4" /> Créer un ticket
										</button>
									)}
								</>
							) : (
								<>
									<span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
										<FiInbox className="h-6 w-6" />
									</span>
									<p className="mt-3 text-sm font-semibold text-slate-600">Aucun ticket ne correspond à ces filtres.</p>
									<p className="mt-1 text-xs text-slate-400">Changez d'onglet ou élargissez la catégorie pour les retrouver.</p>
								</>
							)}
						</div>
					) : (
						items.map((t) => (
							<TicketListCard key={t.id} ticket={t} selected={selectedId === t.id} categoryLabel={categoryLabel} onSelect={setSelectedId} />
						))
					)}
				</div>

				{/* ---- Détail (colonne collante : le dossier reste visible pendant qu'on parcourt la liste) ---- */}
				<div className="lg:sticky lg:top-4">
					{!selectedId ? (
						<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-20 text-center">
							<span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
								<FiInbox className="h-6 w-6" />
							</span>
							<p className="mt-3 text-sm font-semibold text-slate-600">Sélectionnez un ticket</p>
							<p className="mt-1 text-xs text-slate-400">Son détail, ses contenus liés et sa discussion s'afficheront ici.</p>
						</div>
					) : !detail && detailLoading ? (
						<div className="animate-pulse space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
							<div className="flex items-start gap-3">
								<div className="h-11 w-11 rounded-full bg-slate-100" />
								<div className="flex-1 space-y-2">
									<div className="h-3 w-28 rounded bg-slate-100" />
									<div className="h-4 w-2/3 rounded bg-slate-100" />
									<div className="h-3 w-44 rounded bg-slate-100" />
								</div>
							</div>
							<div className="h-20 rounded-xl bg-slate-50" />
							<div className="h-16 rounded-xl bg-slate-50" />
						</div>
					) : !detail ? (
						<div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center">
							<p className="text-sm font-semibold text-slate-600">Ticket introuvable</p>
							<p className="mt-1 text-xs text-slate-400">Il a peut-être été supprimé. Sélectionnez un autre ticket dans la liste.</p>
						</div>
					) : (
						<TicketDetail
							detail={detail}
							categoryLabel={categoryLabel}
							canWrite={canWrite}
							currentUserEmail={currentUserEmail}
							onToggleStatus={setStatusOf}
							onDelete={remove}
							onAssigneesSaved={() => {
								if (selectedId) loadDetail(selectedId);
								loadList();
							}}
							comment={comment}
							onCommentChange={setComment}
							onSendComment={addComment}
							sendingComment={sendingComment}
						/>
					)}
				</div>
			</div>

			<TicketCreateModal
				open={showCreate}
				onClose={() => {
					setShowCreate(false);
					setCreatePrefill(null); // le prochain « Nouveau ticket » manuel repart d'une modale vierge
				}}
				onCreated={(t) => {
					loadList();
					loadCounts();
					setSelectedId(t.id);
				}}
				initialTitle={createPrefill?.title}
				initialDescription={createPrefill?.description}
				linkedContentId={createPrefill?.linkedContentId ?? null}
				linkedContentType={createPrefill?.linkedContentType ?? null}
				linkedContentTitle={createPrefill?.linkedContentTitle ?? null}
			/>
		</div>
	);
};

export default Tickets;
